import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Table, Input, Button, Space, Tooltip, InputNumber, Form, Typography, Popconfirm } from 'antd';
import Highlighter from 'react-highlight-words';
import { SearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { encode } from 'js-base64';

const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
}) => {
    const inputNode = inputType === 'number' ? <InputNumber style={{ width: "100%" }} /> : <Input />;
    return (
        <td {...restProps}>
            {editing ? (
                <Form.Item
                    name={dataIndex}
                    style={{
                        margin: 0,
                    }}
                    rules={[
                        {
                            required: true,
                            message: `Please Input ${title}!`,
                        },
                    ]}
                >
                    {inputNode}
                </Form.Item>
            ) : (
                children
            )}
        </td>
    );
};

export default function DetailTable11({ data, loading, setData, modified, setModified, mark}) {

    const Bucket = "https://d1e9ilirjteg2w.cloudfront.net/";

    var searchInput;

    const [form] = Form.useForm();
    const [editingKey, setEditingKey] = useState('');

    const isEditing = (record) => record.key === editingKey;

    const [state, setState] = useState({
        searchText: '',
        searchedColumn: '',
    });

    const edit = (record) => {
        form.setFieldsValue({
            name: '',
            age: '',
            address: '',
            ...record,
        });
        setEditingKey(record.key);
    };

    const cancel = () => {
        setEditingKey('');
    };

    const save = async (key) => {
        try {
            const currentArr = [...modified];
            const row = await form.validateFields();
            const newData = [...data];
            const index = newData.findIndex((item) => key === item.key);

            if (index > -1) {
                const item = newData[index];
                if (row.l_b !== item.l_b || row.u_b !== item.u_b || row["equation(ID)"] !== item["equation(ID)"] ) {
                    // console.log("mark",key);
                    currentArr.push(key);
                    setModified(unique(currentArr));
                }
                newData.splice(index, 1, { ...item, ...row });
                setData(newData);
                setEditingKey('');
            } else {
                newData.push(row);
                setData(newData);
                setEditingKey('');
            }
        } catch (errInfo) {
            console.log('Validate Failed:', errInfo);
        }
    };

    const getColumnSearchProps = dataIndex => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={node => {
                        searchInput = node;
                    }}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        // size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button onClick={() => handleReset(clearFilters)} style={{ width: 90 }}>
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : '',
        onFilterDropdownVisibleChange: visible => {
            if (visible) {
                setTimeout(() => searchInput.select(), 100);
            }
        },
        render: text =>
            state.searchedColumn === dataIndex ? (
                <Tooltip placement="topLeft" title={
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[state.searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                }>
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[state.searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                </Tooltip>
            ) : (
                <Tooltip placement="topLeft" title={text}>{text}</Tooltip>
            ),
    });

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setState({
            searchText: selectedKeys[0],
            searchedColumn: dataIndex,
        });
    };

    const handleReset = clearFilters => {
        clearFilters();
        setState({ searchText: '' });
    };

    const columns = [
        {
            title: 'Reaction ID',
            dataIndex: 'reaction_id',
            key: 'reaction_id',
            width: 150,
            editable: false,
            sorter: (a, b) => a.reaction_id.localeCompare(b.reaction_id),
            ellipsis: {
                showTitle: false,
            },
            ...getColumnSearchProps('reaction_id'),
        },
        {
            title: 'Equation(ID)',
            dataIndex: 'equation(ID)',
            key: 'equation(ID)',
            width: 250,
            editable: true,
            sorter: (a, b) => a["equation(ID)"].localeCompare(b["equation(ID)"]),
            ellipsis: {
                showTitle: false,
            },
            ...getColumnSearchProps('equation(ID)'),
        },
        {
            title: 'Equation(Name)',
            dataIndex: 'equation(Name)',
            key: 'equation(Name)',
            width: 200,
            editable: false,
            sorter: (a, b) => a["equation(Name)"].localeCompare(b["equation(Name)"]),
            ellipsis: {
                showTitle: false,
            },
            ...getColumnSearchProps('equation(Name)'),
        },
        {
            title: 'l_b',
            dataIndex: 'l_b',
            key: 'l_b',
            width: 100,
            editable: true,
            sorter: (a, b) => a["l_b"] - b["l_b"],
            ellipsis: {
                showTitle: false,
            },
            ...getColumnSearchProps('l_b'),
        },
        {
            title: 'u_b',
            dataIndex: 'u_b',
            key: 'u_b',
            width: 100,
            editable: true,
            sorter: (a, b) => a["u_b"] - b["u_b"],
            ellipsis: {
                showTitle: false,
            },
            ...getColumnSearchProps('u_b'),
        },
        {
            title: 'Operation',
            dataIndex: 'operation',
            width: 250,
            fixed: 'right',
            render: (_, record) => {
                const editable = isEditing(record);
                return editable ? (
                    <span>
                        <Typography.Link
                            onClick={() => save(record.key)}
                            style={{
                                marginRight: 8,
                            }}
                        >
                            Save
                        </Typography.Link>
                        <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                            <a>Cancel</a>
                        </Popconfirm>

                    </span>
                ) : (
                    <Space wrap>
                    <Typography.Link disabled={editingKey !== '' || mark} onClick={() => edit(record)}>
                        Edit
                    </Typography.Link>

                  

                    {/* <Typography.Link
                    onClick={() => handleRemove(record.key)}
                    style={{
                        marginRight: 8,
                    }}
                    >
                    Remove
                    </Typography.Link> */}
                    </Space>
                );
            },
        },
    ];

    const mergedColumns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }

        return {
            ...col,
            onCell: (record) => ({
                record,
                inputType: col.dataIndex === 'l_b' || col.dataIndex === 'u_b' || col.dataIndex === 'flux' ? 'number' : 'text',
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        };
    });

    function unique(arr) {
        var newArr = [...new Set(arr)];
        console.log(newArr);
        return newArr;
    }

    return (
        <>
        <Form form={form} component={false}>
            {/* <div style={{scroll:"auto"}}> */}
            <Table
                // bordered
                components={{
                    body: {
                        cell: EditableCell,
                    },
                }}
                id="antdTable"
                rowKey="reaction_id"
                columns={mergedColumns}
                dataSource={data}
                loading={loading}
                
                rowClassName="editable-row"
                pagination={ data.length > 10 ? true : false}
                scroll={{x:1050}}
            />
            {/* </div> */}
            </Form>

        </>

    );
    // }
}
